import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageTransition from '../../components/PageTransition';
import SafeIcon from '../../common/SafeIcon';
import { FiPlus, FiLink, FiPlayCircle, FiUser, FiClock, FiCheck, FiCopy } from 'react-icons/fi';
import { supabase, TABLES } from '../../supabase/supabase';

const PARTICIPANT_BASE_URL = 'https://interview.rutabaga.app/session';

const STATUS_STYLES = {
  pending:   'bg-blue-50 text-blue-700',
  active:    'bg-green-50 text-green-700',
  complete:  'bg-gray-100 text-gray-600',
};

const STATUS_LABELS = {
  pending:  'Pending',
  active:   'Active',
  complete: 'Complete',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const Sessions = () => {
  const navigate = useNavigate();
  const { projectId } = useParams();

  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const fetchSessions = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from(TABLES.SESSIONS)
        .select('*')
        .eq('project_id', projectId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (err) {
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const createSession = async () => {
    try {
      setCreating(true);
      const { data, error } = await supabase
        .from(TABLES.SESSIONS)
        .insert([{ project_id: projectId, status: 'pending' }])
        .select()
        .single();

      if (error) throw error;
      setSessions((prev) => [data, ...prev]);
    } catch (err) {
      console.error('Error creating session:', err);
      alert('Failed to create session: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (token, sessionId) => {
    const url = `${PARTICIPANT_BASE_URL}/${token}`;
    await navigator.clipboard.writeText(url);
    setCopiedId(sessionId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <PageTransition className="p-8 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-rutabaga-dark">Sessions</h1>
          <p className="text-gray-500 text-sm mt-1">Create sessions and share participant links to begin interviews.</p>
        </div>
        <button
          onClick={createSession}
          disabled={creating}
          className="rutabaga-button-primary flex items-center gap-2"
        >
          <SafeIcon icon={FiPlus} strokeWidth={3} />
          {creating ? 'Creating…' : 'New Session'}
        </button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-gray-100 bg-gray-50 text-xs font-bold text-gray-400 uppercase tracking-widest">
          <div className="col-span-1">#</div>
          <div className="col-span-3">Participant Link</div>
          <div className="col-span-3">Created</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-3 text-right">Actions</div>
        </div>

        {loading ? (
          <div className="p-10 text-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-rutabaga-pink mx-auto"></div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-12 h-12 rounded-full bg-rutabaga-bg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiUser} className="text-gray-400 text-xl" />
            </div>
            <p className="font-bold text-rutabaga-dark">No sessions yet</p>
            <p className="text-gray-400 text-sm mt-1">Create a session to generate a participant link.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {sessions.map((session, i) => {
              const participantUrl = `${PARTICIPANT_BASE_URL}/${session.participant_token}`;
              const isCopied = copiedId === session.id;

              return (
                <div key={session.id} className="grid grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-gray-50 transition-colors">
                  <div className="col-span-1 text-sm text-gray-400 font-mono">
                    {sessions.length - i}
                  </div>

                  <div className="col-span-3 flex items-center gap-2 min-w-0">
                    <SafeIcon icon={FiLink} className="text-gray-300 shrink-0" />
                    <span className="text-xs text-gray-500 truncate font-mono">/session/{session.participant_token?.slice(0, 8)}…</span>
                  </div>

                  <div className="col-span-3 flex items-center gap-2 text-sm text-gray-500">
                    <SafeIcon icon={FiClock} className="text-gray-300 shrink-0" />
                    {formatDate(session.created_at)}
                  </div>

                  <div className="col-span-2">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${STATUS_STYLES[session.status] || STATUS_STYLES.pending}`}>
                      {STATUS_LABELS[session.status] || session.status}
                    </span>
                  </div>

                  <div className="col-span-3 flex items-center justify-end gap-2">
                    <button
                      onClick={() => copyLink(session.participant_token, session.id)}
                      className="flex items-center gap-1.5 text-xs font-bold text-gray-500 hover:text-rutabaga-dark bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-md transition-colors"
                    >
                      <SafeIcon icon={isCopied ? FiCheck : FiCopy} className={isCopied ? 'text-green-600' : ''} />
                      {isCopied ? 'Copied!' : 'Copy Link'}
                    </button>

                    {session.status !== 'complete' && (
                      <button
                        onClick={() => navigate(`/projects/${projectId}/sessions/${session.id}/active`)}
                        className="flex items-center gap-1.5 text-xs font-bold text-white bg-rutabaga-pink hover:bg-rutabaga-pink-hover px-3 py-1.5 rounded-md transition-colors"
                      >
                        <SafeIcon icon={FiPlayCircle} />
                        {session.status === 'active' ? 'Resume' : 'Start'}
                      </button>
                    )}

                    {session.status === 'complete' && (
                      <button className="text-xs font-bold text-gray-400 hover:text-gray-600 px-3 py-1.5 transition-colors">
                        View Data
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PageTransition>
  );
};

export default Sessions;
